import { generatePath } from 'react-router-dom';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import isEqual from 'lodash.isequal';
import {
  Pagination,
  Response,
  BaseTransaction,
  GeneralLedgerTransaction,
} from '@ui/src';
import { incomingTransactionsListApi } from '../services';
import { statusesTransactionsApi } from '../../../../store/statuses/services';
import { transactionApi } from '../../../transactions/store/services';
import { StatusUpdate } from '../../../transactions/type';
import ROUTES from '../../../../routes';

export interface IncomingListState {
  transactions?: BaseTransaction[];
  pagination?: Pagination;
  currentTransactionId?: string;
  expandedTransactionId?: string;
  takeCount?: number;
  collapseAllRows?: boolean;
}

const initialState: IncomingListState = {
  transactions: undefined,
};

export const incomingListSlice = createSlice({
  name: 'incomingList',
  initialState,
  reducers: {
    setExpandedTransactionId: (state, action: PayloadAction<string>) => {
      state.expandedTransactionId = action.payload;
    },
    setCollapseAllRows: (state, action: PayloadAction<boolean>) => {
      state.collapseAllRows = action.payload;
    },
    clearIncomingListState: (state) => {
      state.transactions = undefined;
      state.pagination = undefined;
      state.expandedTransactionId = undefined;
      state.takeCount = undefined;
    },
    setTakeCount: (state, action: PayloadAction<number>) => {
      state.takeCount = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      incomingTransactionsListApi.endpoints.getIncomingList.matchFulfilled,
      (state, action: PayloadAction<Response<BaseTransaction>>) => {
        const nextTransactions = action.payload.data.edges.map((item) => ({
          ...item.node,
          deletedAt: item?.node?.deletedAt || '',
          url: generatePath(ROUTES.INCOMING_TRANSACTION, {
            transactionId: item.node.id,
          }),
          generalLedgerTransactions: {
            edges: [],
            pageInfo: undefined,
          },
          values: [],
        }));

        state.transactions = nextTransactions;

        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      incomingTransactionsListApi.endpoints.updateIncomingTransaction
        .matchFulfilled,
      (state, action: PayloadAction<{ data: BaseTransaction }>) => {
        const nextTransactions = state?.transactions?.map((item) => {
          const result = { ...item };

          if (result.id === action.payload.data.id) {
            result.paymentType = action.payload.data.paymentType;
            result.pspPartnerId = action.payload.data.pspPartnerId;
            result.pspPartnerAccountNumber =
              action.payload.data.pspPartnerAccountNumber;
            result.documentNumber = action.payload.data.documentNumber;
            result.info = action.payload.data.info;
            result.acceptedIn = action.payload.data.acceptedIn;
            result.recipientAccountNumber =
              action.payload.data.recipientAccountNumber;
            result.recipientAccount = action.payload.data.recipientAccount;
          }

          return result;
        });

        state.transactions = nextTransactions;
      },
    );
    builder.addMatcher(
      incomingTransactionsListApi.endpoints.getIncomingListStatuses
        .matchFulfilled,
      (state, action: PayloadAction<Response<StatusUpdate>>) => {
        const { edges } = action.payload.data;
        const nextTransactions = state?.transactions?.map((item) => {
          const result = { ...item };

          const currentTransactionStatus = edges.find(
            (transaction) => transaction.node.id === result.id,
          );

          if (
            currentTransactionStatus &&
            currentTransactionStatus.node.baseellaStatus !==
              result.baseellaStatus
          ) {
            result.baseellaStatus =
              currentTransactionStatus.node.baseellaStatus;
          }

          return result;
        });

        if (!isEqual(nextTransactions, state.transactions)) {
          state.transactions = nextTransactions;
        }
      },
    );
    builder.addMatcher(
      incomingTransactionsListApi.endpoints.createIncomingTransaction
        .matchFulfilled,
      (state, action: PayloadAction<{ data: BaseTransaction }>) => {
        let nextTransactions: BaseTransaction[] = [];

        if (state.transactions?.length) {
          nextTransactions = ([] as BaseTransaction[]).concat(
            state.transactions,
          );
        }

        nextTransactions.unshift({
          ...action.payload.data,
          deletedAt: action.payload.data?.deletedAt || '',
          url: generatePath(ROUTES.INCOMING_TRANSACTION, {
            transactionId: action.payload.data.id,
          }),
          generalLedgerTransactions: {
            edges: [],
            pageInfo: undefined,
          },
          values: [],
        });

        state.transactions = nextTransactions;

        if (state.takeCount) {
          state.takeCount += 1;
        }
      },
    );
    builder.addMatcher(
      statusesTransactionsApi.endpoints.changeTransactionStatus.matchFulfilled,
      (state, action: PayloadAction<{ data: BaseTransaction }>) => {
        if (state.transactions) {
          const nextTransactions = state.transactions.map((item) => {
            const result = { ...item };

            if (result.id === action.payload.data.id) {
              result.baseellaStatus = action.payload.data.baseellaStatus;
            }

            return result;
          });

          state.transactions = nextTransactions;
        }
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getGeneralLedgerTransactionValues.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<GeneralLedgerTransaction>> & {
          meta: {
            arg: {
              originalArgs: string;
            };
          };
        },
      ) => {
        if (state.transactions) {
          const nextGeneralLedgerTransactions = {
            edges: action.payload.data.edges.map((item) => ({
              cursor: item.cursor,
              node: {
                ...item.node,
                url: generatePath(ROUTES.INCOMING_TRANSACTION_GL, {
                  glId: item.node.id,
                }),
              },
            })),
            pageInfo: action.payload.data.pageInfo,
          };

          const nextTransactions = state.transactions.map((item) => {
            const result = { ...item };

            if (result.id === action.meta.arg.originalArgs) {
              result.generalLedgerTransactions = nextGeneralLedgerTransactions;
            }

            return result;
          });

          state.transactions = nextTransactions;
        }
      },
    );
  },
});

export const {
  clearIncomingListState,
  setExpandedTransactionId,
  setTakeCount,
  setCollapseAllRows,
} = incomingListSlice.actions;

export default incomingListSlice.reducer;
